import { Button, Toggle } from '@prio365/prio365-react-library';
import {
  Col,
  Divider,
  Form,
  Input,
  notification,
  Radio,
  RadioChangeEvent,
  Row,
  Space,
  Typography,
} from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
  getCompaniesByIdState,
  getCurrentFolderItem,
  getCurrentRemoteItem,
  getInternalProjectContactsData,
  getProject,
  getProjectByIdState,
  getUserMeContactId,
  RootReducerState,
} from '../../../../apps/main/rootReducer';
import Flex from '../../../../components/Flex';
import {
  CreateDocumentFromTemplateRequest,
  CreateDocumentFromTemplatePlaceholder,
} from '../../../../models/Document';
import { DocumentTemplate } from '../../../../models/DocumentTemplate';
import { DriveItem } from '../../../../models/Drive';
import { Project } from '../../../../models/Project';
import {
  CompanyId,
  DocumentTemplateId,
  DriveItemId,
  GroupId,
  OfficeId,
  ProjectId,
} from '../../../../models/Types';
import { PrioTheme } from '../../../../theme/types';
import { makePrioStyles } from '../../../../theme/utils';
import { colon, rowGutter } from '../../../../util/forms';
import CompanyPicker from '../../../companies/components/CompanyPicker';
import OfficePicker from '../../../companies/components/OfficePicker';
import { CompaniesByIdState } from '../../../companies/reducers/companies';
import ContactPicker from '../../../contacts/components/ContactPicker';
import ProjectPhasePicker from '../../../projects/components/ProjectPhasePicker';
import ProjectPicker from '../../../projects/components/ProjectPicker';
import { useProjectContacts } from '../../../projects/hooks/useProjectContacts';
import { ProjectByIdState } from '../../../projects/reducers/projects';
import {
  driveItemCreated,
  DriveItemsFetchContext,
  fetchDriveItemsSagaAction,
} from '../../actions';
import {
  fetchExternalProjectContacts,
  fetchInternalProjectContacts,
} from '../../../projects/actions';
import {
  apiCreateDocumentFromTemplate,
  apiCreateUploadSession,
  apiDownloadCreatedDocument,
  apiDownloadRawDocumentTemplateFile,
  apiFetchDocument,
  apiFetchDocumentPrefix,
  DocumentTemplateRawDownloadFormat,
} from '../../api';
import { checkFileNameExists } from '../../util';
import { FileNameConflictBehaviour } from '../Modals/DriveItemConflictModal';
import FolderSelectionDrawer from '../Drawers/FolderSelectionDrawer';
import DriveItemBreadcrumb from '../DriveItemBreadcrumb/DriveItemBreadcrumb';
import DocumentTemplateFormSelector from './DocumentTemplateFormSelector';
import DebouncedInput from '../DebouncedInput';
import useContactsContext from '../../../contacts/hooks/useContactsProvider';

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
    flex: 1,
  },
  newDocument: {
    height: 'calc(100% - 294px)',
  },
  projectPickerWidth: {
    width: '100%',
  },
  prefixLabel: {
    whiteSpace: 'nowrap',
  },
  targetFolderBreadcrumb: {
    marginTop: theme.old.spacing.unit(1),
  },
  disabled: {
    opacity: 0.3,
    pointerEvents: 'none',
  },
  editTargetFolderIcon: {
    color: `${theme.old.palette.primaryColor}!important`,
  },
  noTemplateSelected: {
    margin: `${theme.old.spacing.unit(3)}px 0`,
  },
  spacing: {
    marginBottom: theme.old.spacing.defaultPadding,
  },
  buttonSpacing: {
    marginRight: theme.old.spacing.defaultPadding,
  },
  fullHeight: {
    height: '100%',
    overflow: 'hidden',
  },
  flex: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
  },
  radioGroup: {
    overflow: 'hidden',
    width: '100%',
    '& .ant-space-vertical': {
      width: '100%',
    },
    '& .ant-radio-wrapper': {
      overflow: 'hidden',
      width: '100%',
    },
    '& .ant-radio-wrapper > span:last-child': {
      display: 'flex',
      overflow: 'hidden',
      width: '100%',
    },
  },
  warning: {
    color: theme.colors.base.red[80],
  },
  formSelectorFullHeight: {
    height: '100%',
  },
  boldFont: {
    fontWeight: 600,
  },
  noData: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    padding: theme.spacing.regular,
  },
  drawerButtons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
  },
}));

type PlaceholderEntityType =
  | 'internalOffice'
  | 'company'
  | 'office'
  | 'contact'
  | 'projectMember'
  | 'projectPhase'
  | 'freetext';

interface PlaceholderPickerDescriptor {
  formFieldName: string;
  entityType: PlaceholderEntityType;
  groupDisplayName?: string;
  group?: string;
  placeholderName?: string;
}

const ROOT_ENTITY_TO_TYPE: Record<string, PlaceholderEntityType> = {
  internaloffice: 'internalOffice',
  company: 'company',
  office: 'office',
  contact: 'contact',
  projectmember: 'projectMember',
  projectphase: 'projectPhase',
  businessunit: 'office',
};

/** Auto-resolved root entities that don't need a form field */
const AUTO_RESOLVED_ENTITIES = new Set(['project', 'creator']);

/** StaticPlaceholder sub-types that are resolved server-side (no user input needed) */
const AUTO_RESOLVED_STATIC_PLACEHOLDERS = new Set(['today']);

/** Determines which pickers/inputs to show based on template placeholder paths and groups */
const getRequiredPickerGroups = (
  template: DocumentTemplate
): PlaceholderPickerDescriptor[] => {
  if (!template?.placeholders) return [];

  const seen = new Set<string>();
  const descriptors: PlaceholderPickerDescriptor[] = [];

  for (const placeholder of template.placeholders) {
    const path = placeholder.path || '';
    const rootEntity = path.split('.')[0]?.toLowerCase();
    const entityType = ROOT_ENTITY_TO_TYPE[rootEntity];
    const group = placeholder.group;

    if (entityType) {
      // Entity picker (company, contact, office, etc.)
      const dedupKey = group ? `${entityType}__${group}` : entityType;
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);

      descriptors.push({
        formFieldName: dedupKey,
        entityType,
        groupDisplayName: group || undefined,
        group: group || undefined,
      });
    } else if (!AUTO_RESOLVED_ENTITIES.has(rootEntity)) {
      // For staticplaceholder, skip only auto-resolved sub-types (e.g. Today)
      if (rootEntity === 'staticplaceholder') {
        const subType = path.split('.')[1]?.toLowerCase();
        if (AUTO_RESOLVED_STATIC_PLACEHOLDERS.has(subType)) continue;
      }
      // Freetext placeholder — each gets its own input
      const dedupKey = `freetext__${placeholder.name}`;
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);

      descriptors.push({
        formFieldName: dedupKey,
        entityType: 'freetext',
        placeholderName: placeholder.name,
      });
    }
  }

  return descriptors;
};

const mainOfficeSelector = (internalCompanyId: CompanyId) =>
  createSelector<[(state: RootReducerState) => CompaniesByIdState], OfficeId>(
    (state) => getCompaniesByIdState(state),
    (companyByIds) => {
      return (
        companyByIds[internalCompanyId]?.offices?.find(
          (office) => office.isMainOffice
        )?.officeId ?? null
      );
    }
  );

const internalCompanySelector = (selectedProjectId: ProjectId) =>
  createSelector<[(state: RootReducerState) => ProjectByIdState], CompanyId>(
    (state) => getProjectByIdState(state),
    (projectsById) =>
      projectsById ? projectsById[selectedProjectId]?.subsidiaryId : null
  );

export interface DocumentTemplateFormWrapperRef {
  onFinish: VoidFunction;
}

interface DocumentTemplateFormWrapperProps {
  className?: string;
  parentFolderDriveItem?: DriveItem;
  projectId?: ProjectId;
  groupId?: GroupId;
  showOnlyReports?: boolean;
  projectPickerFilter?: (project: Project) => boolean;
  onSuccess?: (targetFolder: DriveItem, projectId: ProjectId) => void;
  onStartCreateDocument?: () => void;
  onCancel?: () => void;
  onChange?: () => void;
  onThumbnailURLChange?: (thumbnailURL: String[]) => void;
  onSelectedTemplateChange?: (selectedTemplate: any) => void;
  setIsFormValid?: (isFormValid: boolean) => void;
  setPrefix?: (prefix: string) => void;
  setNameIsValid?: (isValid: boolean) => void;
  setNameExists?: (exists: boolean) => void;
}

export const DocumentTemplateFormWrapper = forwardRef(
  (
    props: DocumentTemplateFormWrapperProps,
    ref: React.Ref<DocumentTemplateFormWrapperRef>
  ) => {
    const {
      className,
      parentFolderDriveItem,
      projectId,
      showOnlyReports,
      projectPickerFilter,
      onStartCreateDocument,
      onSuccess,
      onCancel,
      onThumbnailURLChange,
      onSelectedTemplateChange = () => {},
      setIsFormValid,
      setPrefix: setPrefixProp,
      setNameIsValid: setNameIsValidProp = () => {},
      setNameExists: setNameExistsProp = () => {},
    } = props;

    const theme = useTheme<PrioTheme>();
    //#region ------------------------------------------------- Defaults
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    //#endregion

    //#region  ------------------------------------------------- States / Attributes / Selectors
    const { getContactById } = useContactsContext();
    const currentRemoteItem = useSelector(getCurrentRemoteItem);

    const [selectedProjectId, setSelectedProjectId] =
      useState<ProjectId>(projectId);
    const [saveDestinationProjectId, setSaveDestinationProjectId] =
      useState<ProjectId>(projectId);
    const internalCompanyIdFromProjectId = useSelector(
      internalCompanySelector(selectedProjectId)
    );
    const mainOfficeId = useSelector(
      mainOfficeSelector(internalCompanyIdFromProjectId)
    );
    const [prefix, setPrefix] = useState<string>(
      moment().format('YYYY-MM-DD_')
    );
    const [templateId, setTemplateId] = useState<DocumentTemplateId>();
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const [targetFolderId, setTargetFolderId] = useState<DriveItemId>(
      parentFolderDriveItem?.id
    );

    const targetFolder = useSelector<RootReducerState, DriveItem>((state) =>
      getCurrentFolderItem(state, targetFolderId)
    );

    const [saveFolderDriveItemId, setSaveFolderDriveItemId] =
      useState<DriveItemId>(targetFolderId);

    const [saveFolderChoice, setSaveFolderChoice] = useState<
      'customFolder' | string
    >('customFolder');
    const [
      selectTargetFolderDrawerVisible,
      setSelectTargetFolderDrawerVisible,
    ] = useState<boolean>(false);
    const [collapsed, setCollapsed] = useState<Boolean>(true);
    const [documentTemplates, setDocumentTemplates] = useState<
      DocumentTemplate[]
    >([]);
    const [isMounted, setIsMounted] = useState(false);
    const userId = useSelector(getUserMeContactId);
    const internalProjectContactsData = useSelector(
      getInternalProjectContactsData
    );
    const {
      externalProjectContactContacts,
      externalProjectContacts: externalProjectContactsList,
    } = useProjectContacts(selectedProjectId);
    const [projectMemberIdMap, setProjectMemberIdMap] = useState<
      Record<string, string>
    >({});

    const projectForSavePath = useSelector<RootReducerState, Project>((state) =>
      getProject(state, saveDestinationProjectId)
    );

    const [documentName, setDocumentName] = useState<string>('');
    const [sourceProjectId, setSourceProjectId] =
      useState<ProjectId>(projectId);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [hasProjectPhases, setHasProjectPhases] = useState<boolean>(false);
    const [nameIsValid, setNameIsValid] = useState<boolean>(false);
    const [nameExists, setNameExists] = useState<boolean>(false);
    const [directDownload, setDirectDownload] = useState<boolean>(false);
    const [createPdf, setCreatePdf] = useState<boolean>(false);
    const saveDestinationInitialized = useRef<boolean>(!!projectId);

    //#endregion

    //#region  ------------------------------------------------- Memos
    const pickerDescriptors = useMemo(
      () => (selectedTemplate ? getRequiredPickerGroups(selectedTemplate) : []),
      [selectedTemplate]
    );

    const hasAnyPlaceholderPickers = pickerDescriptors.length > 0;

    const initialValues = useMemo(() => {
      if (!selectedTemplate) return null;
      const values: Record<string, string | string[]> = {
        documentName: '',
        saveFolderDriveItemId: '',
        templateTags: [],
        projectId: selectedProjectId,
        templateId: selectedTemplate.templateId,
      };
      for (const desc of pickerDescriptors) {
        if (desc.entityType === 'internalOffice' && !desc.group) {
          values[desc.formFieldName] = mainOfficeId || '';
        } else {
          values[desc.formFieldName] = '';
        }
      }
      return values;
    }, [selectedProjectId, selectedTemplate, mainOfficeId, pickerDescriptors]);
    //#endregion

    //#region  ------------------------------------------------- Effects
    useEffect(() => {
      if (templateId) {
        const newSelectedTemplate =
          templateId &&
          documentTemplates.find((t) => t.templateId === templateId);
        setSelectedTemplate(newSelectedTemplate);
      }
    }, [templateId, documentTemplates]);

    useEffect(() => {
      if (selectedProjectId) {
        dispatch(fetchExternalProjectContacts(selectedProjectId));
        dispatch(fetchInternalProjectContacts(selectedProjectId));
      }
    }, [dispatch, selectedProjectId]);

    useEffect(() => {
      if (selectedProjectId) {
        setSelectedProjectId(selectedProjectId);
      }
    }, [selectedProjectId]);
    useEffect(() => {
      onSelectedTemplateChange(selectedTemplate);
    }, [selectedTemplate, onSelectedTemplateChange]);

    useEffect(() => {
      async function fetchPrefix() {
        const { data } = await apiFetchDocumentPrefix(saveDestinationProjectId);
        if (data) {
          setPrefix(data.value);
          if (setPrefixProp) {
            setPrefixProp(data.value);
          }
        }
      }

      if (sourceProjectId) {
        fetchPrefix();
      }
    }, [saveDestinationProjectId, sourceProjectId, setPrefixProp]);

    useEffect(() => {
      setIsMounted(true);
      return () => setIsMounted(false);
    }, []);

    useEffect(() => {
      if (projectId) {
        setSelectedProjectId(projectId);

        if (!saveDestinationInitialized.current) {
          setSaveDestinationProjectId(projectId);
          saveDestinationInitialized.current = true;
        }
      }
    }, [projectId]);

    useEffect(() => {
      if (targetFolder) {
        setTargetFolderId(targetFolder.id);
      }
    }, [targetFolder]);

    useEffect(() => {
      form.resetFields();
    }, [initialValues, form]);

    useEffect(() => {
      let objectUrl: string | null = null;

      async function getThumbnails() {
        if (onThumbnailURLChange) onThumbnailURLChange([]);
        const templateIdToUse =
          selectedTemplate?.templateId || selectedTemplate?.documentTemplateId;
        const response = await apiDownloadRawDocumentTemplateFile(
          templateIdToUse,
          DocumentTemplateRawDownloadFormat.HTML
        );

        if (response && response.data) {
          const typedBlob = response.data.slice(
            0,
            response.data.size,
            'text/html'
          );
          objectUrl = URL.createObjectURL(typedBlob);
          if (onThumbnailURLChange) onThumbnailURLChange([objectUrl]);
        }
      }

      const templateIdToUse =
        selectedTemplate?.templateId || selectedTemplate?.documentTemplateId;
      if (selectedTemplate && templateIdToUse) {
        getThumbnails();
      }

      return () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      };
    }, [selectedTemplate, onThumbnailURLChange]);

    const validateFormState = React.useCallback(async () => {
      const requiredFieldsValid =
        !!saveFolderDriveItemId &&
        !!templateId &&
        !!documentName &&
        nameIsValid &&
        !nameExists;
      if (!requiredFieldsValid) {
        setIsFormValid(false);
        return;
      }
      try {
        await form.validateFields();
        setIsFormValid(true);
      } catch {
        setIsFormValid(false);
      }
    }, [
      saveFolderDriveItemId,
      templateId,
      documentName,
      nameIsValid,
      nameExists,
      form,
      setIsFormValid,
    ]);

    useEffect(() => {
      validateFormState();
    }, [
      form,
      templateId,
      saveFolderDriveItemId,
      documentName,
      nameIsValid,
      nameExists,
      validateFormState,
    ]);

    useEffect(() => {
      if (selectedProjectId) {
        form.setFieldsValue({ documentName: documentName });
      }
    }, [selectedProjectId, documentName, form]);

    useImperativeHandle(ref, () => ({
      onFinish: form.submit,
    }));
    //#endregion

    //#region  ------------------------------------------------- helper methods
    const renderPickerForEntityType = (desc: PlaceholderPickerDescriptor) => {
      switch (desc.entityType) {
        case 'internalOffice':
          return <OfficePicker onlyInternal disabled={isSaving} />;
        case 'company':
          return <CompanyPicker disabled={isSaving} />;
        case 'office':
          return <OfficePicker disabled={isSaving} />;
        case 'contact':
          return <ContactPicker disabled={isSaving} />;
        case 'projectMember':
          const projectContacts = [
            ...(internalProjectContactsData[selectedProjectId] ?? [])
              .map((pc) => getContactById(pc.contactId))
              .filter(Boolean),
            ...externalProjectContactContacts,
          ];

          const filterFn = (contact) =>
            internalProjectContactsData[selectedProjectId]?.some(
              (pc) => pc.contactId === contact.contactId
            ) ||
            externalProjectContactsList.some(
              (pc) => pc.contactId === contact.contactId
            );

          const handleOnChange = (contactId: string | string[]) => {
            const id = Array.isArray(contactId) ? contactId[0] : contactId;
            form.setFieldsValue({ [desc.formFieldName]: id });
            const internalContacts =
              internalProjectContactsData[selectedProjectId] || [];
            const internalMatch = internalContacts.find(
              (pc) => pc.contactId === id
            );
            if (internalMatch?.internalProjectContactId) {
              setProjectMemberIdMap((prev) => ({
                ...prev,
                [id]: internalMatch.internalProjectContactId,
              }));
            } else {
              const externalMatch = externalProjectContactsList.find(
                (pc) => pc.contactId === id
              );
              if (externalMatch?.externalProjectContactId) {
                setProjectMemberIdMap((prev) => ({
                  ...prev,
                  [id]: externalMatch.externalProjectContactId,
                }));
              }
            }
            void handleFormValuesChange();
          };

          return (
            <ContactPicker
              projectId={selectedProjectId}
              disabled={isSaving}
              includedContacts={projectContacts}
              filter={filterFn}
              onChange={handleOnChange}
            />
          );
        case 'projectPhase':
          return (
            <ProjectPhasePicker
              projectId={selectedProjectId}
              onChange={(val: string) =>
                form.setFieldsValue({ [desc.formFieldName]: val })
              }
              disabled={isSaving || !hasProjectPhases}
              setHasProjectPhases={setHasProjectPhases}
            />
          );
        case 'freetext':
          return <Input.TextArea rows={3} disabled={isSaving} />;
        default:
          return null;
      }
    };

    const onCallFinished = () => {
      onSuccess(targetFolder, selectedProjectId);
      if (isMounted) {
        setIsSaving(false);
        onCreateDocumentSuccess();
      }
    };

    const startCreateDocument = () => {
      notification.open({
        message: t('common:info'),
        description: t(
          'documents:newDocumentForm.successMessages.startCreateDocument'
        ),
      });
      if (onStartCreateDocument) {
        onStartCreateDocument();
      }
    };
    //#endregion

    //#region  ------------------------------------------------- onChangeEvents
    const onTemplateIdChange = (templateId: DocumentTemplateId) => {
      setTemplateId(templateId);
      if (setIsFormValid) {
        setIsFormValid(false);
      }
    };
    const updateCollapsed = () => {
      setCollapsed(!collapsed);
    };
    const handleFormValuesChange = async () => {
      if (setIsFormValid) {
        try {
          await form.validateFields();
          setIsFormValid(saveFolderDriveItemId ? true : false);
        } catch (error) {
          setIsFormValid(false);
        }
      }
    };

    //#endregion

    //#region  ------------------------------------------------- handleEvents
    const handleOnBreadcrumbItemClick = (driveItemId: DriveItemId) => {
      setTargetFolderId(driveItemId);
      setSaveFolderDriveItemId(driveItemId);
      if (!!projectForSavePath) {
        const { groupId, projectId } = projectForSavePath;
        dispatch(
          fetchDriveItemsSagaAction(
            projectId,
            groupId,
            driveItemId,
            driveItemId === null,
            DriveItemsFetchContext.Other
          )
        );
      }
    };

    const openTargetFolderDrawer = () => {
      setSelectTargetFolderDrawerVisible(true);
    };

    const handleTargetFolderDrawerOk = (
      targetFolder: DriveItem | null,
      drawerSelectedProjectId?: ProjectId
    ) => {
      setSelectTargetFolderDrawerVisible(false);
      setTargetFolderId(targetFolder?.id);
      setSaveFolderDriveItemId(targetFolder?.id);
      if (drawerSelectedProjectId) {
        setSaveDestinationProjectId(drawerSelectedProjectId);
      }
    };
    const handleTargetFolderDrawerCancel = () => {
      setSelectTargetFolderDrawerVisible(false);
    };

    const handleProjectChange = (givenProjectId: ProjectId) => {
      setSelectedProjectId(givenProjectId);
      setSourceProjectId(givenProjectId);
      setProjectMemberIdMap({});
      if (setIsFormValid) {
        setIsFormValid(false);
      }
      // Only set saveDestinationProjectId on first selection
      if (!saveDestinationInitialized.current) {
        setSaveDestinationProjectId(givenProjectId);
        saveDestinationInitialized.current = true;
      }
    };

    const onCreateDocumentSuccess = () => {};
    const handleSaveFolderChoice = (e: RadioChangeEvent) => {
      setSaveFolderChoice(e.target.value);
      if (e.target.value !== 'customFolder') {
        setSaveFolderDriveItemId(e.target.value);
      } else {
        setTargetFolderId(targetFolder !== null ? targetFolder?.id : null);
        setSaveFolderDriveItemId(
          targetFolder !== null ? targetFolder?.id : null
        );
      }
    };

    const handleOnFinish = async (formValues: Record<string, string>) => {
      const seenGroups = new Set<string>();
      const placeholders: CreateDocumentFromTemplatePlaceholder[] = [];

      for (const p of selectedTemplate?.placeholders || []) {
        const path = p.path || '';
        const rootEntity = path.split('.')[0]?.toLowerCase();
        const group = p.group;
        const hasGroup = !!group; // empty string → false

        // Resolve entityId based on root entity type
        let entityId = '';
        const entityType = ROOT_ENTITY_TO_TYPE[rootEntity];
        if (entityType) {
          const formFieldName = hasGroup
            ? `${entityType}__${group}`
            : entityType;
          entityId = formValues[formFieldName] || '';

          // ContactPicker stores contactId, but backend expects internalProjectContactId or externalProjectContactId
          if (entityType === 'projectMember' && entityId) {
            if (projectMemberIdMap[entityId]) {
              entityId = projectMemberIdMap[entityId];
            } else {
              console.warn(
                `Missing projectMemberId mapping for contactId: ${entityId}`
              );
            }
          }
        } else if (rootEntity === 'project') {
          entityId = selectedProjectId || '';
        } else if (rootEntity === 'creator') {
          entityId = userId || '';
        } else if (rootEntity === 'staticplaceholder') {
          const subType = path.split('.')[1]?.toLowerCase();
          if (!AUTO_RESOLVED_STATIC_PLACEHOLDERS.has(subType)) {
            // Other staticplaceholder types (e.g. FreeText) — treated as freetext input
            entityId = formValues[`freetext__${p.name}`] || '';
          }
        } else {
          // Freetext placeholder — value from text input
          entityId = formValues[`freetext__${p.name}`] || '';
        }

        if (hasGroup) {
          // Grouped: deduplicate by group, send only { entityId, group }
          if (!seenGroups.has(group)) {
            seenGroups.add(group);
            placeholders.push({ entityId, group });
          }
        } else {
          // Ungrouped: send { entityId, name }
          placeholders.push({ entityId, name: p.name });
        }
      }

      const request: CreateDocumentFromTemplateRequest = {
        placeholders,
        tables: [],
      };

      setIsSaving(true);
      startCreateDocument();

      try {
        const createResponse = await apiCreateDocumentFromTemplate(
          templateId,
          request
        );

        if (!createResponse.data || !Array.isArray(createResponse.data)) {
          notification.error({
            message: t('common:error'),
            description: t(
              'documents:newDocumentForm.errorMessages.createDocumentError'
            ),
          });
          return;
        }

        const fileLocations: string[] = createPdf
          ? createResponse.data.filter((loc: string) =>
              loc.toLowerCase().endsWith('.pdf')
            )
          : createResponse.data.filter(
              (loc: string) => !loc.toLowerCase().endsWith('.pdf')
            );

        const destinationFolderId =
          saveFolderDriveItemId ?? targetFolderId ?? targetFolder?.id;
        const destinationGroupId = projectForSavePath?.groupId;
        const destinationProjectId =
          saveDestinationProjectId ?? selectedProjectId;

        for (const fileLocation of fileLocations) {
          const downloadResponse =
            await apiDownloadCreatedDocument(fileLocation);
          if (!downloadResponse.data) continue;

          const blob = downloadResponse.data;
          const extension = fileLocation.split('.').pop() || '';
          const fileName = `${prefix ?? ''}${documentName.trim()}.${extension}`;

          if (
            destinationFolderId &&
            destinationGroupId &&
            destinationProjectId &&
            blob.size > 0
          ) {
            const uploadSessionResponse = await apiCreateUploadSession(
              destinationProjectId,
              destinationGroupId,
              destinationFolderId,
              fileName,
              FileNameConflictBehaviour.RENAME
            );

            if (uploadSessionResponse.data?.uploadUrl) {
              const uploadResponse = await fetch(
                uploadSessionResponse.data.uploadUrl,
                {
                  method: 'PUT',
                  headers: {
                    'Content-Length': blob.size.toString(),
                    'Content-Range': `bytes 0-${blob.size - 1}/${blob.size}`,
                  },
                  body: blob,
                }
              );

              // Fetch full DriveItem and add directly to Redux state for immediate UI update
              if (uploadResponse.ok) {
                const uploadedItem = await uploadResponse.json();
                const { data: fullDriveItem } = await apiFetchDocument(
                  destinationGroupId,
                  uploadedItem.id
                );
                if (fullDriveItem) {
                  dispatch(
                    driveItemCreated(
                      fullDriveItem,
                      destinationFolderId,
                      destinationGroupId
                    )
                  );
                }
              }
            }
          }

          if (directDownload) {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }
        }

        onCallFinished();
      } catch {
        notification.error({
          message: t('common:error'),
          description: t(
            'documents:newDocumentForm.errorMessages.createDocumentError'
          ),
        });
      } finally {
        if (isMounted) {
          setIsSaving(false);
        }
      }
    };

    const handleDocumentNameChange = (value: string) => {
      const isValid =
        !!value.match(/^[^<>:"/\\|?*]+$/) && value.trim().length > 0;
      setNameIsValid(isValid);
      setNameIsValidProp(isValid);
    };
    //#endregion

    //#region ------------------------------------------------- BODY
    //#endregion

    //#region ------------------------------------------------- Effects
    useEffect(() => {
      const checkFileNames = async () => {
        const rawExtension = selectedTemplate?.fileExtension
          ? selectedTemplate.fileExtension
          : selectedTemplate?.name?.includes('.')
            ? selectedTemplate.name.split('.').pop()
            : null;

        // Normalize: ensure extension always starts with a dot
        const selectedTemplateType = rawExtension
          ? rawExtension.startsWith('.')
            ? rawExtension
            : `.${rawExtension}`
          : null;

        let extensionsToCheck = [selectedTemplateType ?? '.xlsx'];

        // Aus .dotx wird nach dem erstellen .docx, deshalb müssen wir auch .docx prüfen
        if (selectedTemplateType === '.dotx') {
          extensionsToCheck.push('.docx');
        }

        // Aus .xltx wird nach dem erstellen .xlsx, deshalb müssen wir auch .xlsx prüfen
        if (selectedTemplateType === '.xltx') {
          extensionsToCheck.push('.xlsx');
        }

        const fullNames = extensionsToCheck.map(
          (ext) => `${prefix ?? ''}${documentName.trim()}${ext}`
        );

        const destinationFolderId =
          saveFolderDriveItemId ?? targetFolderId ?? targetFolder?.id;

        if (
          !destinationFolderId ||
          !projectForSavePath ||
          !documentName.trim()
        ) {
          return;
        }

        let fileExists = false;
        for (const fullName of fullNames) {
          if (
            await checkFileNameExists(
              projectForSavePath?.groupId,
              saveFolderDriveItemId ?? targetFolderId,
              fullName
            )
          ) {
            fileExists = true;
            break;
          }
        }
        setNameExists(fileExists);
        setNameExistsProp(fileExists);
      };
      checkFileNames();
    }, [
      saveFolderDriveItemId,
      documentName,
      projectForSavePath,
      setNameExistsProp,
      prefix,
      targetFolderId,
      targetFolder,
      selectedTemplate,
    ]);

    //#region ------------------------------------------------- BODY
    return (
      <>
        <Flex.Row className={classNames(classes.root, className)}>
          <Flex.Column flex={3}>
            <DocumentTemplateFormSelector
              className={classNames({
                [classes.formSelectorFullHeight]: !(
                  templateId && selectedProjectId
                ),
              })}
              isSaving={false}
              showOnlyReports={showOnlyReports}
              onTemplateIdChange={onTemplateIdChange}
              onTemplatesLoaded={setDocumentTemplates}
              onCancel={onCancel}
              onChange={updateCollapsed}
              cancelable={!(templateId && selectedProjectId)}
            >
              {!projectId && (
                <>
                  <Form layout="vertical">
                    <Flex.Row>
                      <Flex.Item flex={1}>
                        <Row gutter={theme.old.spacing.unit(rowGutter)}>
                          <Col span={24}>
                            <Form.Item
                              label={t(
                                'documents:newDocumentForm.labels.project'
                              )}
                            >
                              <ProjectPicker
                                filter={projectPickerFilter}
                                value={selectedProjectId}
                                onChange={handleProjectChange}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Flex.Item>
                    </Flex.Row>
                  </Form>
                </>
              )}
            </DocumentTemplateFormSelector>
            {templateId && selectedProjectId && (
              <>
                <Divider style={{ marginTop: '8px' }} />
                <Typography.Title level={5}>
                  {t('documents:documentTemplates.generalHeadline')}
                </Typography.Title>
                <Form
                  form={form}
                  onFinish={handleOnFinish}
                  onValuesChange={handleFormValuesChange}
                  layout="vertical"
                  initialValues={initialValues}
                  id={'prio-project-new-documents-form'}
                >
                  <Flex.Row className={classes.fullHeight}>
                    <Flex.Item flex={1} className={classes.flex}>
                      <div
                        style={{
                          flex: 1,
                        }}
                      >
                        <Row gutter={theme.old.spacing.unit(rowGutter)}>
                          <Col span={24}>
                            <Form.Item
                              name="documentName"
                              validateTrigger="onBlur"
                              colon={colon}
                              label={t(
                                'documents:newDocumentForm.labels.documentName'
                              )}
                              style={{ marginBottom: '8px' }}
                              rules={[
                                {
                                  required: true,
                                  validator: async (_, value) => {
                                    if (!value) {
                                      return Promise.reject(
                                        t(
                                          'documents:newDocumentForm.validation.missingDocumentName'
                                        )
                                      );
                                    }

                                    const isValid =
                                      !!value.match(/^[^<>:"/\\|?*]+$/) &&
                                      value.trim().length > 0;
                                    if (!isValid) {
                                      return Promise.reject(
                                        t(
                                          'documents:confirmation.rename.invalidFileName'
                                        )
                                      );
                                    }

                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <DebouncedInput
                                disabled={isSaving}
                                value={documentName}
                                onChange={(value) => {
                                  handleDocumentNameChange(value);
                                  setDocumentName(value);
                                }}
                                prefix={prefix}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        {hasAnyPlaceholderPickers && (
                          <>
                            <Divider style={{ marginTop: '8px' }} />
                            <Typography.Title level={5}>
                              {t(
                                'documents:documentTemplates.placeholderHeadline'
                              )}
                            </Typography.Title>
                          </>
                        )}
                        {pickerDescriptors.map((desc) => (
                          <Row
                            gutter={theme.old.spacing.unit(rowGutter)}
                            key={desc.formFieldName}
                          >
                            <Col span={24}>
                              <Form.Item
                                name={desc.formFieldName}
                                validateTrigger="onBlur"
                                colon={colon}
                                label={
                                  desc.entityType === 'freetext'
                                    ? desc.placeholderName
                                    : desc.groupDisplayName
                                      ? `${t(`documents:newDocumentForm.labels.${desc.entityType}`)} - ${desc.groupDisplayName}`
                                      : t(
                                          `documents:newDocumentForm.labels.${desc.entityType}`
                                        )
                                }
                                rules={[
                                  {
                                    required:
                                      desc.entityType === 'freetext'
                                        ? false
                                        : desc.entityType === 'projectPhase'
                                          ? hasProjectPhases
                                          : true,
                                    message: t(
                                      'documents:newDocumentForm.validation.missingPlaceholderChoice'
                                    ),
                                  },
                                ]}
                              >
                                {renderPickerForEntityType(desc)}
                              </Form.Item>
                            </Col>
                          </Row>
                        ))}
                        <Divider style={{ marginTop: '8px' }} />
                        <Typography.Title level={5}>
                          {t(
                            'documents:newDocumentForm.labels.saveFolderChoice'
                          )}
                        </Typography.Title>
                        <Flex.Row
                          childrenGap={theme.old.spacing.unit(4)}
                          style={{ marginBottom: theme.old.spacing.unit(2) }}
                        >
                          <Toggle
                            checked={directDownload}
                            onChange={setDirectDownload}
                            size="small"
                            disabled={isSaving}
                          >
                            {t(
                              'documents:newDocumentForm.labels.directDownload'
                            )}
                          </Toggle>
                          <Toggle
                            checked={createPdf}
                            onChange={setCreatePdf}
                            size="small"
                            disabled={isSaving}
                          >
                            {t('documents:newDocumentForm.labels.createPdf')}
                          </Toggle>
                        </Flex.Row>
                        <Row gutter={theme.old.spacing.unit(rowGutter)}>
                          <Col span={24}>
                            <Form.Item
                              label={
                                <div>
                                  {t(
                                    `documents:newDocumentForm.labels.targetFolder`
                                  )}
                                  <Button
                                    onClick={openTargetFolderDrawer}
                                    iconProp={['fal', 'pen']}
                                    type="link"
                                  ></Button>
                                </div>
                              }
                            >
                              {
                                <Radio.Group
                                  value={saveFolderChoice}
                                  onChange={handleSaveFolderChoice}
                                  disabled={isSaving}
                                  className={classes.radioGroup}
                                >
                                  <Space direction="vertical">
                                    <Radio
                                      value="customFolder"
                                      key="customFolder"
                                    >
                                      <DriveItemBreadcrumb
                                        driveItem={
                                          targetFolder ?? parentFolderDriveItem
                                        }
                                        remoteItem={currentRemoteItem}
                                        project={projectForSavePath}
                                        onItemClick={
                                          handleOnBreadcrumbItemClick
                                        }
                                        disableLinkNavigation
                                      />
                                    </Radio>
                                  </Space>
                                </Radio.Group>
                              }
                            </Form.Item>
                          </Col>
                        </Row>
                        {!saveFolderDriveItemId && (
                          <p className={classes.warning}>
                            {t(
                              `documents:newDocumentForm.labels.noFolderSelected`
                            )}
                          </p>
                        )}
                      </div>
                    </Flex.Item>
                  </Flex.Row>
                </Form>
              </>
            )}
          </Flex.Column>
        </Flex.Row>
        {projectForSavePath && (
          <FolderSelectionDrawer
            visible={selectTargetFolderDrawerVisible}
            onOk={handleTargetFolderDrawerOk}
            onCancel={handleTargetFolderDrawerCancel}
            projectId={saveDestinationProjectId}
            selectedFolder={targetFolder?.id ?? parentFolderDriveItem?.id}
            allowProjectChange
            setProjectId={setSaveDestinationProjectId}
          />
        )}
      </>
    );
    //#endregion
  }
);

export default DocumentTemplateFormWrapper;
